import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';

import cloneDeep from 'lodash.clonedeep';
import xor from 'lodash.xor';
import {NumberInput} from 'src/components/input';
import {MessageContext} from 'src/contexts/message-context';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {MAX_CONTRACT_FIELD_VALUE} from 'src/modules/profile/account/constants';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import BoxLoading from '@pb/kit-component-box-loading';
import Button from '@pb/kit-component-button';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import DropDownTree from '@pb/kit-component-dropdown-tree';
import {Integer} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import Typography from '@pb/kit-component-typography';
import {LocaleContext} from '@pb/kit-context-locale';
import {stringToNumber} from '@pb/kit-formatters';

import {ChangesInfo} from '../../../../components/changes-info';
import {TabContent} from '../../../../components/tab-content';
import {CreditsList} from '../components/credits-list';

import {parseUsers} from './utils';

import {
    apiV2Alerts,
    apiV2AlertsTable,
    apiV2AlertsTableCell,
    apiV2AlertsTableRow,
    apiV2AlertsUnavailableMessage,
} from './alerts.view.module.styl';

import type {ChangeTreeType} from '@pb/kit-component-dropdown-tree/dist/typedef';
import type {Contract} from 'src/openapi/admin/models/Contract';
import type {CreditAlertSettings} from 'src/openapi/admin/models/CreditAlertSettings';
import type {UserList} from 'src/openapi/admin/models/UserList';


type Props = {
    data: CreditAlertSettings,
    users: UserList,
    activeContracts: Array<Contract>,
    alertsBusy: boolean,
    onUpdateAlerts: (alerts: CreditAlertSettings) => Promise<void>,
    onCloseError: () => void,
    isCrmIntegration?: boolean,
};

export const AlertsView = (props: Props) => {
    const {alertsBusy, data: {changesInfo}, isCrmIntegration} = props;
    const {locale} = useContext(LocaleContext);
    const [creditValue, setCreditValue] = useState('');
    const [userIds, setUserIds] = useState<Array<number>>(props.data.userIds);
    const [userIdsStr, setUserIdsStr] = useState<Array<string>>(props.data.userIds.map(String));
    const [activeContractId, setActiveContractId] = useState<number>(0);
    const [alertsInfo, setAlertsInfo] = useState(props.data.alertsInfo);
    const [isChanged, setIsChanged] = useState(false);
    const users = parseUsers(props.users);
    const {setInfoMessage} = useContext(MessageContext);

    const isAddDisabled = !creditValue || !activeContractId;
    const hasActiveContracts = !!props.activeContracts.length;
    const hasCredits = !!alertsInfo.length;
    const isSaveDisabled = !isChanged;

    const params = useParams<{accountId: string}>();
    const accountId = params.accountId ? +params.accountId : -1;
    const {canEditApiV2} = useAdminAccessRight();

    const onCreditsChange = useCallback((value?: number) => setCreditValue(
        value ? value.toString() : '',
    ), []);

    const onContractChange = useCallback((value: number) => setActiveContractId(value), []);

    const onRemoveCredit = useCallback((contractId: number, credit: number) => {
        const newAlerts = cloneDeep(alertsInfo);
        const alertsByContract = newAlerts.find(creditAlertInfo => creditAlertInfo.contractId === contractId);

        if (alertsByContract) {
            alertsByContract.creditsBelow = alertsByContract.creditsBelow
                .filter(currentCredit => currentCredit !== credit);
            setAlertsInfo(newAlerts);
        }
    }, [alertsInfo]);

    const onUsersChange = useCallback(({checkedIdsList}: ChangeTreeType) => {
        setUserIdsStr(checkedIdsList);
    }, []);

    const onSave = () => {
        props.onUpdateAlerts({
            alertsInfo,
            userIds: userIds.map(Number),
        });
    };

    const onAdd = useCallback(() => {
        const newAlerts = cloneDeep(alertsInfo);
        const idx = newAlerts.findIndex(({contractId}) => activeContractId === contractId);
        const value = stringToNumber(creditValue, locale);

        if (value === 0) {
            setInfoMessage('Sorry, the alert cannot be added. Number of credits must be greater than 0');
            setCreditValue('');
            return;
        }

        if (idx === -1) {
            newAlerts.push({
                contractId: activeContractId,
                creditsBelow: [value],
            });
        } else if (!newAlerts[idx].creditsBelow.includes(value)) {
            newAlerts[idx].creditsBelow.push(value);
            newAlerts[idx].creditsBelow.sort((a, b) => b - a);
        }

        setActiveContractId(0);
        setCreditValue('');
        setAlertsInfo(newAlerts);
    }, [alertsInfo, creditValue, activeContractId, locale, setInfoMessage]);

    useEffect(() => {
        const usersChanged = !!xor(props.data.userIds, userIds).length;
        const alertsChanged = !isDeepEqual({prev: props.data.alertsInfo, current: alertsInfo});
        const isDataChanged = usersChanged || alertsChanged;

        if (isDataChanged) {
            setIsChanged(true);
        } else if (!isDataChanged && isChanged) {
            setIsChanged(false);
        }
    }, [alertsInfo, userIds, props.data, isChanged]);

    useEffect(() => {
        setUserIds(userIdsStr.map(Number));
    }, [userIdsStr]);

    return (
        <BoxLoading loading={alertsBusy}>
            <TabContent>
                <Group direction="v" gap="l" alignItems="start">
                    <Typography fontWeight="semi-bold" variant="heading-level-3">Alerts</Typography>
                    <div style={{width: 350}}>
                        <Group direction="v" gap="l">
                            {isCrmIntegration ? (
                                <div className={apiV2AlertsUnavailableMessage}>
                                    Alerts aren't available for CRM Integration <br/> pricing model
                                </div>
                            ) : (
                                <Group className={apiV2Alerts} direction="v" gap="l">
                                    <Item>
                                        <Label>Alerts set up for</Label>
                                        <DropDownTree
                                            searchable={true}
                                            data={users}
                                            checkedIdsList={userIdsStr}
                                            inputProps={{
                                                placeholder: 'Select Users',
                                                disabled: !hasActiveContracts || !canEditApiV2(accountId),
                                            }}
                                            onTreeChange={onUsersChange}
                                        />
                                    </Item>
                                    <div className={apiV2AlertsTable}>
                                        <div className={apiV2AlertsTableRow}>
                                            <div className={apiV2AlertsTableCell}>
                                                <Label>Alert when</Label>
                                            </div>
                                            <div className={apiV2AlertsTableCell}>
                                                <Label>credits below</Label>
                                            </div>
                                        </div>
                                        {hasCredits ? (
                                            <CreditsList
                                                {...{
                                                    data: alertsInfo,
                                                    contracts: props.activeContracts,
                                                    onRemove: onRemoveCredit,
                                                    disabled: !canEditApiV2(accountId),
                                                }}
                                            />
                                        ) : null}
                                        <div className={apiV2AlertsTableRow}>
                                            <div className={apiV2AlertsTableCell}>
                                                <div style={{width: hasActiveContracts ? 186 : 240}}>
                                                    <ComboBox
                                                        placeholder={hasActiveContracts
                                                            ? 'Contract #'
                                                            : 'There are no active contracts'}
                                                        onChange={onContractChange}
                                                        disabled={!hasActiveContracts || !canEditApiV2(accountId)}
                                                        value={activeContractId}>
                                                        {props.activeContracts.map(
                                                            ({contractId, contractNumber}) => (
                                                                <ComboboxItem
                                                                    key={contractId}
                                                                    value={contractId}
                                                                    caption={contractNumber}
                                                                />
                                                            ),
                                                        )}
                                                    </ComboBox>
                                                </div>
                                            </div>
                                            <div className={apiV2AlertsTableCell}>
                                                <NumberInput
                                                    width={148}
                                                    value={creditValue ? +creditValue : undefined}
                                                    onBlur={onCreditsChange}
                                                    readOnly={!canEditApiV2(accountId)}
                                                    disabled={!hasActiveContracts}
                                                    maxValue={MAX_CONTRACT_FIELD_VALUE}
                                                    placeholder="# of credits"
                                                    displayedValue={creditValue
                                                        ? Integer.format(+creditValue, locale)
                                                        : ''}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </Group>
                            )}
                            <Group justify="space-between">
                                <Button variant="Type.SECONDARY" disabled={isAddDisabled} onClick={onAdd}>Add</Button>
                                <Button variant="Type.PRIMARY" disabled={isSaveDisabled} onClick={onSave}>Save</Button>
                            </Group>
                            <ChangesInfo data={changesInfo}/>
                        </Group>
                    </div>
                </Group>
            </TabContent>
        </BoxLoading>
    );
};
